import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { brandTokens } from '@softwareiq/brand-ui';

import { resolveCompanyAssetUrl } from '../TickerIcon';
import { ChartPresentationProvider } from '../../charts/common/ChartPresentationContext';
import {
  getCompanyDisplayName,
  isPrivateCompanyIdentifier,
} from '../../../utils/companyHelpers';

const softwareIqLogoLight = require('../../../data/images/logo.png');
const softwareIqLogoDark = require('../../../data/images/logodark.png');

const readBlobAsDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error('Unable to read company logo.'));
  reader.readAsDataURL(blob);
});

const useEmbeddedCompanyLogo = ({ ticker, themeMode }) => {
  const [state, setState] = useState({ ready: false, dataUrl: null });
  const logoUrl = useMemo(() => resolveCompanyAssetUrl({
    ticker,
    prefer: 'logos',
    mode: themeMode,
  }), [themeMode, ticker]);

  useEffect(() => {
    let active = true;
    setState({ ready: false, dataUrl: null });

    if (!logoUrl) {
      setState({ ready: true, dataUrl: null });
      return () => { active = false; };
    }

    fetch(logoUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Company logo request failed (${response.status}).`);
        return response.blob();
      })
      .then(readBlobAsDataUrl)
      .then((dataUrl) => {
        if (active) setState({ ready: true, dataUrl });
      })
      .catch(() => {
        // Export canvases cannot safely consume a cross-origin image. Fall back
        // to the ticker badge so a missing logo never blocks the PNG download.
        if (active) setState({ ready: true, dataUrl: null });
      });

    return () => { active = false; };
  }, [logoUrl]);

  return state;
};

function CompanyBrand({ ticker, companyName, dataUrl, themeMode, onLogoError }) {
  const foreground = themeMode === 'dark' ? '#f8fafc' : '#172033';
  const displayName = getCompanyDisplayName(ticker, companyName);
  const tickerLabel = isPrivateCompanyIdentifier(ticker)
    ? displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 3).toUpperCase() || 'CO'
    : String(ticker || '?').slice(0, 4).toUpperCase();
  const tickerBoxWidth = Math.max(52, 22 + tickerLabel.length * 12);

  if (dataUrl) {
    return (
      <Box
        component="img"
        src={dataUrl}
        alt={`${displayName} logo`}
        onError={onLogoError}
        sx={{ display: 'block', maxWidth: 260, width: 'auto', height: 52, objectFit: 'contain' }}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{
        width: tickerBoxWidth,
        minWidth: tickerBoxWidth,
        height: 52,
        borderRadius: 2,
        display: 'grid',
        placeItems: 'center',
        bgcolor: themeMode === 'dark' ? '#253047' : '#e8eef8',
        color: foreground,
        boxSizing: 'border-box',
        px: 1,
        fontSize: tickerLabel.length >= 4 ? 16 : 18,
        fontWeight: 800,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}>
        {tickerLabel}
      </Box>
      <Typography sx={{ color: foreground, fontSize: 24, fontWeight: 750 }}>
        {displayName}
      </Typography>
    </Box>
  );
}

const getFrameSpacing = (width) => ({
  outer: Math.round(width * 0.045),
  headerGap: Math.round(width * 0.018),
});

function EntityBrand({ entity, ticker, companyName, dataUrl, themeMode, onLogoError }) {
  const resolvedKind = entity?.kind || (ticker ? 'company' : 'product');
  if (resolvedKind === 'company') {
    return (
      <CompanyBrand
        ticker={ticker || entity?.ticker}
        companyName={companyName || entity?.label}
        dataUrl={dataUrl}
        themeMode={themeMode}
        onLogoError={onLogoError}
      />
    );
  }

  const isDark = themeMode === 'dark';
  const label = entity?.label;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.25, minWidth: 0 }}>
      <Box
        component="img"
        src={isDark ? softwareIqLogoDark : softwareIqLogoLight}
        alt="SoftwareIQ"
        sx={{ display: 'block', width: 190, height: 'auto', flexShrink: 0 }}
      />
      {label ? (
        <>
          <Box sx={{ width: '1px', height: 36, bgcolor: isDark ? '#2b354a' : '#d7deea' }} />
          <Typography sx={{ color: isDark ? '#f8fafc' : '#172033', fontSize: 22, fontWeight: 700 }}>
            {label}
          </Typography>
        </>
      ) : null}
    </Box>
  );
}

function ChartShareFrame({
  width,
  height,
  themeMode,
  ticker,
  companyName,
  entity,
  title,
  subtitle,
  sourceLabel = 'SoftwareIQ',
  metadata,
  presentation,
  chartState,
  renderChart,
  onReadyChange,
  frameRef,
}) {
  const exportTheme = useMemo(() => createTheme({
    palette: { mode: themeMode },
    typography: { fontFamily: brandTokens.fontFamily },
  }), [themeMode]);
  const resolvedTicker = ticker || entity?.ticker;
  const isCompanyEntity = (entity?.kind || (resolvedTicker ? 'company' : 'product')) === 'company';
  const { ready: logoReady, dataUrl: logoDataUrl } = useEmbeddedCompanyLogo({
    ticker: isCompanyEntity ? resolvedTicker : null,
    themeMode,
  });
  const [logoRenderFailed, setLogoRenderFailed] = useState(false);
  const [chartReady, setChartReady] = useState(false);
  const chartContainerRef = useRef(null);
  const [plotBounds, setPlotBounds] = useState({ width: 0, height: 0 });
  const spacing = getFrameSpacing(width);
  const isDark = themeMode === 'dark';
  const background = isDark ? '#101522' : '#ffffff';
  const foreground = isDark ? '#f8fafc' : '#172033';
  const secondary = isDark ? '#aeb8cb' : '#5f6b7c';
  const plotWidth = plotBounds.width || Math.max(1, width - spacing.outer * 2);
  const plotHeight = plotBounds.height || Math.max(1, Math.round(height * 0.62));

  useEffect(() => {
    onReadyChange?.(logoReady && chartReady && plotBounds.width > 0 && plotBounds.height > 0);
  }, [chartReady, logoReady, onReadyChange, plotBounds.height, plotBounds.width]);

  useLayoutEffect(() => {
    const node = chartContainerRef.current;
    if (!node) return undefined;
    const updateBounds = () => {
      const rect = node.getBoundingClientRect?.();
      const next = {
        width: Math.round(node.clientWidth || rect?.width || Math.max(1, width - spacing.outer * 2)),
        height: Math.round(node.clientHeight || rect?.height || Math.max(1, height * 0.62)),
      };
      if (next.width <= 0 || next.height <= 0) return;
      setPlotBounds((current) => (
        current.width === next.width && current.height === next.height ? current : next
      ));
    };
    updateBounds();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateBounds);
    observer.observe(node);
    return () => observer.disconnect();
  }, [height, metadata, spacing.outer, subtitle, title, width]);

  useEffect(() => {
    setChartReady(false);
    const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    const cancel = window.cancelAnimationFrame || window.clearTimeout;
    const updateReady = () => {
      const node = chartContainerRef.current;
      setChartReady(Boolean(node && (
        node.querySelector('svg, canvas, [data-chart-export-ready="true"]')
        || node.textContent?.trim()
      )));
    };
    const observer = typeof MutationObserver === 'undefined' || !chartContainerRef.current
      ? null
      : new MutationObserver(updateReady);
    observer?.observe(chartContainerRef.current, { childList: true, subtree: true });
    let secondFrame;
    const firstFrame = schedule(() => {
      secondFrame = schedule(() => {
        updateReady();
      });
    });
    return () => {
      cancel(firstFrame);
      if (secondFrame) cancel(secondFrame);
      observer?.disconnect();
    };
  }, [chartState, height, plotBounds.height, plotBounds.width, presentation, renderChart, themeMode, width]);

  useEffect(() => {
    setLogoRenderFailed(false);
  }, [logoDataUrl]);

  return (
    <ThemeProvider theme={exportTheme}>
      <Box
        ref={frameRef}
        data-testid="chart-share-frame"
        sx={{
          boxSizing: 'border-box',
          width,
          height,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: background,
          color: foreground,
          px: `${spacing.outer}px`,
          pt: `${spacing.outer}px`,
          pb: `${Math.round(spacing.outer * 0.65)}px`,
          fontFamily: brandTokens.fontFamily,
        }}
      >
        <Box sx={{ minHeight: 52, display: 'flex', alignItems: 'center' }}>
          <EntityBrand
            entity={entity}
            ticker={ticker}
            companyName={companyName}
            dataUrl={logoRenderFailed ? null : logoDataUrl}
            themeMode={themeMode}
            onLogoError={() => setLogoRenderFailed(true)}
          />
        </Box>

        <Box sx={{ mt: `${spacing.headerGap}px`, mb: `${Math.round(spacing.headerGap * 0.9)}px` }}>
          <Typography sx={{ color: foreground, fontSize: width >= 1400 ? 42 : 36, lineHeight: 1.12, fontWeight: 780 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography sx={{ color: secondary, fontSize: width >= 1400 ? 22 : 19, lineHeight: 1.35, mt: 1 }}>
              {subtitle}
            </Typography>
          ) : null}
          {metadata ? (
            <Typography sx={{ color: secondary, fontSize: width >= 1400 ? 18 : 16, lineHeight: 1.35, mt: 1.25 }}>
              {metadata}
            </Typography>
          ) : null}
        </Box>

        <ChartPresentationProvider value={{
          ...presentation,
          labelMode: chartState?.labelMode ?? presentation?.labelMode,
          mode: 'export',
          static: true,
          width: plotWidth,
          height: plotHeight,
          frameWidth: width,
          frameHeight: height,
          themeMode,
        }}>
          <Box
            ref={chartContainerRef}
            data-testid="chart-share-plot"
            sx={{
              flex: '1 1 0',
              minHeight: 0,
              minWidth: 0,
              width: '100%',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'stretch',
              '& > *': {
                flex: '1 1 auto',
                width: '100% !important',
                height: '100% !important',
                maxWidth: '100% !important',
                maxHeight: '100% !important',
                minWidth: '0 !important',
                minHeight: '0 !important',
              },
              '& svg, & canvas': {
                display: 'block',
                maxWidth: '100%',
                maxHeight: '100%',
              },
              '& [data-chart-watermark="true"], & img[alt="SoftwareIQ Logo"], & img[alt="Website Logo"]': {
                display: 'none !important',
              },
            }}
          >
            <Box
              data-testid="chart-share-rendered-content"
              sx={{
                width: '100%',
                height: '100%',
                minWidth: 0,
                minHeight: 0,
                position: 'relative',
                overflow: 'hidden',
                '& > .MuiDivider-root': { display: 'none' },
                '& > *:not(.MuiDivider-root)': {
                  width: '100% !important',
                  height: '100% !important',
                  maxWidth: '100% !important',
                  maxHeight: '100% !important',
                  minWidth: '0 !important',
                  minHeight: '0 !important',
                },
              }}
            >
              {renderChart?.({
                width: plotWidth,
                height: plotHeight,
                frameWidth: width,
                frameHeight: height,
                themeMode,
                presentation: {
                  ...presentation,
                  mode: 'export',
                  static: true,
                  width: plotWidth,
                  height: plotHeight,
                  frameWidth: width,
                  frameHeight: height,
                },
                chartState,
                theme: exportTheme,
              })}
            </Box>
          </Box>
        </ChartPresentationProvider>

        <Box sx={{
          flex: '0 0 auto',
          minHeight: 38,
          mt: `${Math.round(spacing.headerGap * 0.7)}px`,
          pt: `${Math.round(spacing.headerGap * 0.7)}px`,
          borderTop: `1px solid ${isDark ? '#2b354a' : '#dde3ec'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Typography sx={{ color: secondary, fontSize: 15 }}>
            Source: {sourceLabel}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Typography sx={{ color: secondary, fontSize: 14, fontWeight: 600 }}>
              Powered by
            </Typography>
            <Box
              component="img"
              src={isDark ? softwareIqLogoDark : softwareIqLogoLight}
              alt="SoftwareIQ"
              sx={{ display: 'block', width: width >= 1400 ? 190 : 165, height: 'auto' }}
            />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default ChartShareFrame;
